import { useRequest } from 'ahooks'
import { Table, message } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getNameLink } from '@/components/special-project-relations.component'
import { ProjectWeekWorkResourceSnapshotType, ResourceType } from '@/schemas'

import { UserResourceApportionDetail } from '../user-resource-dashboard.schemas'

export const BigProjectDetailTable = React.forwardRef<
  { download: () => void },
  { selfId: string; resourceType: ResourceType }
>(({ selfId, resourceType }, ref) => {
  const { t } = useTranslation()

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const { data, run } = useRequest(
    {
      url: '/api/work-resource/statistics/specific-project/detail/infos',
      params: {
        startDate,
        endDate,
        selfId,
        resourceType,
      },
    },
    {
      manual: true,
      initialData: [] as UserResourceApportionDetail[],
    }
  )

  React.useImperativeHandle(ref, () => {
    return {
      download() {
        if (!startDate || !endDate) {
          message.warn('起始或截止日期不存在')
          return
        }
        const a = document.createElement('a')
        const params = new URLSearchParams()
        params.append('startDate', startDate)
        params.append('endDate', endDate)
        params.append('selfId', selfId)
        params.append('resourceType', resourceType.toString())
        a.href = `/api/work-resource/statistics/specific-project/detail/download?${params.toString()}`
        document.body.appendChild(a)
        a.click()
        a.remove()
      },
    }
  }, [startDate, endDate, selfId])

  React.useEffect(() => {
    if (selfId && startDate && endDate) {
      run()
    }
  }, [selfId, startDate, endDate])

  const columns: any[] = [
    {
      title: t('标签'),
      width: 200,
      dataIndex: 'title',
      fixed: 'left',
      render: (_: any, record: any) => {
        if (
          [ResourceType.CUSTOMER, ResourceType.DEVLOP, ResourceType.PRODUCTION].includes(record.project?.resourceType)
        ) {
          return (
            <span className='font-medium text-lg'>
              {getNameLink({
                name: record.project.name,
                type: record.project.resourceType,
                selfId: record.project.selfId,
                params: { tab: 'dashboard', startDate, endDate },
              })}
            </span>
          )
        }

        return record.project.resourceType === ProjectWeekWorkResourceSnapshotType.PROJECT
          ? getNameLink({
              name: record.project.name,
              type: ResourceType.PROJECT,
              selfId: record.project.selfId,
            })
          : record.project.name
      },
    },
  ]

  const _weeks = (data || []).find((d) => !!d.weeks.length)?.weeks
  _weeks &&
    _weeks.forEach((week: any) => {
      columns.push({
        title: `${week.year}.${week.week} ${t('week')}`,
        children: [
          {
            title: t('分摊后工时'),
            key: `${week.year}.${week.week}.totalWorkload`,
            width: 120,
            render: (_: any, record: any, index: number) => {
              const workload = Number(
                record.weeks.find((w: any) => w.year === week.year && w.week === week.week)?.workload ?? 0
              ).toFixed(1)
              return index === 0 ? <span className='font-medium text-lg'>{workload}</span> : workload
            },
          },
          {
            title: t('分摊后工时占比'),
            key: `${week.year}.${week.week}.percentage`,
            width: 120,
            render: (_: any, record: any, index: number) => {
              const percentage = record.weeks.find((w: any) => w.year === week.year && w.week === week.week)?.percentage
              const percentageText = `${percentage ? (Number(percentage) * 100).toFixed(1) : 0}%`
              return index === 0 ? <span className='font-medium text-lg'>{percentageText}</span> : percentageText
            },
          },
        ],
      })
    })

  columns.push({
    title: t('user resource total'),
    children: [
      {
        title: t('分摊后工时'),
        key: 'totalWorkload',
        fixed: 'right',
        dataIndex: 'totalWorkload',
        width: 100,
        render: (value: any, _: any, index: number) => {
          const workload = Number(value || 0).toFixed(1)
          return index === 0 ? <span className='font-medium text-lg'>{workload}</span> : workload
        },
      },
      {
        title: t('分摊后工时占比'),
        key: 'percentage',
        fixed: 'right',
        width: 100,
        render: (_: any, record: any, index: number) => {
          const percentage = `${(Number(record.percentage) * 100).toFixed(1)}%`
          return index === 0 ? <span className='font-medium text-lg'>{percentage}</span> : percentage
        },
      },
    ],
  })

  return <Table columns={columns} dataSource={data} pagination={false} scroll={{ x: 'fit-content', y: 600 }} />
})
